import React from 'react';
import { ArrowRight } from 'lucide-react';
import { bundles } from '../../data/blackout';
import { useBundle } from '../../contexts/BundleContext';
import { formatPrice } from '../../utils/currency';
import { scrollToSection } from '../../utils/scroll';
import type { BundleId } from '../../types/blackout';
export function ValueLadder() {
  const {
    selectBundle,
    showCrystalBonus
  } = useBundle();
  const [single, triple] = bundles;
  const rows = [{
    label: 'Masks',
    a: '1',
    b: '3'
  }, {
    label: 'You pay',
    a: `${formatPrice(single.price)} + shipping`,
    b: formatPrice(triple.price)
  }, {
    label: 'Per mask',
    a: formatPrice(single.price),
    b: formatPrice(triple.price / triple.count)
  }, {
    label: 'Shipping',
    a: 'Applies',
    b: 'Free (U.S.)'
  }, ...(showCrystalBonus && triple.bonus ? [{
    label: 'Bonus',
    a: '—',
    b: 'Color crystal set'
  }] : [])];
  const choose = (id: BundleId) => {
    selectBundle(id);
    scrollToSection('bundles');
  };
  return <section aria-labelledby="ladder-title" className="bg-carbon py-20 md:py-24" data-id="element-356" data-mp="10x1yx7ams3xa">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-12 lg:gap-16" data-id="element-357" data-mp="10uz3g7qplil6">
        <div className="lg:col-span-6" data-id="element-358" data-mp="vwdxrssou6pc">
          <h2 id="ladder-title" className="font-display text-5xl uppercase leading-[0.95] text-bone md:text-6xl" data-id="element-359" data-mp="1and38uy88f4p">
            One mask or three?
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-steel" data-id="element-360" data-mp="1jn07rpm9611z">
            For $30 more you get two more masks, a lower price per mask and
            free shipping. The math makes the call.
          </p>

          <table className="mt-8 w-full border-collapse text-left text-sm" data-id="element-361" data-mp="1tko3oivlhelq">
            <thead data-id="element-362" data-mp="1ommklxy65fa9">
              <tr data-id="element-363" data-mp="1oo7gbqw7z49p">
                <th scope="col" className="sr-only" data-id="element-364" data-mp="1onnva1b9vzrg">
                  Compare
                </th>
                <th scope="col" className="w-[34%] pb-3 font-display text-xl uppercase text-steel" data-id="element-365" data-mp="3vdlzmnko4naw">
                  1 mask
                </th>
                <th scope="col" className="w-[36%] bg-bone px-3 pb-3 pt-3 font-display text-xl uppercase text-obsidian" data-id="element-366" data-mp="jxin4tojys11">
                  3 masks
                </th>
              </tr>
            </thead>
            <tbody data-id="element-367" data-mp="szrwhg834fcu">
              {rows.map(row => <tr key={row.label} className="border-t border-gunmetal" data-id="element-368" data-mp="xx1rrci5nl8l">
                  <th scope="row" className="py-3 pr-3 font-medium text-steel" data-id="element-369" data-mp="12w4mo44f8b2u">
                    {row.label}
                  </th>
                  <td className="py-3 pr-3 text-bone/70" data-id="element-370" data-mp="16w5eqrkg0ksd">{row.a}</td>
                  <td className="border-x border-bone/50 px-3 py-3 font-semibold text-bone" data-id="element-371" data-mp="1bnohif5xu0sj">
                    {row.b}
                  </td>
                </tr>)}
              <tr data-id="element-372" data-mp="1lmcnugok8rge">
                <td data-id="element-373" data-mp="1lmw5rxe7nfuf" />
                <td data-id="element-374" data-mp="1lnfc0gui0fsm" />
                <td className="border-x border-b border-bone/50 p-3" data-id="element-375" data-mp="1lnxf9xfkvua1">
                  <button type="button" onClick={() => choose(triple.id)} className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-bone underline decoration-steel underline-offset-4 hover:decoration-bone" data-id="element-376" data-mp="1qmij06u1gw38">
                    Pick 3 <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" data-id="element-377" data-mp="317ekxxlsdtbk" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-6 lg:pt-2" data-id="element-378" data-mp="3tt7r8h6ajq33">
          <h3 className="font-display text-3xl uppercase leading-none text-bone md:text-4xl" data-id="element-379" data-mp="3ouczuqu9ct7d">
            Every step is $30.
          </h3>
          <p className="mt-3 text-[15px] text-steel" data-id="element-380" data-mp="3k2ljta8qa5vh">Each step up gets you more masks for the same $30.</p>

          <ol className="mt-8 border-l border-gunmetal" data-id="element-381" data-mp="3f4tffgqwcrae">
            {bundles.slice(1).map((bundle, i) => {
            const prev = bundles[i];
            return <li key={bundle.id} className="relative pb-8 pl-8 last:pb-0" data-id="element-382" data-mp="bnbhc10xeyn6" data-mpi={i}>
                  <span aria-hidden="true" className="absolute -left-[5px] top-2 h-2.5 w-2.5 bg-bone" data-id="element-383" data-mp="3sb7paoqna5x8" />
                  <button type="button" onClick={() => choose(bundle.id)} className="group w-full text-left" data-id="element-384" data-mp="133db13znys3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-steel" data-id="element-385" data-mp="3neqtd8d7en9l">
                      {prev.count} → {bundle.count} masks · {bundle.name}
                    </p>
                    <p className="mt-2 font-display text-3xl uppercase leading-none text-bone md:text-4xl" data-id="element-386" data-mp="jy0hwdyuri0i">
                      +{formatPrice(Math.round(bundle.price - prev.price))}
                      <span className="text-steel" data-id="element-387" data-mp="a4jovc6ji6xw"> = +{bundle.count - prev.count} masks</span>
                    </p>
                    {i === 0 && <p className="mt-2 text-sm text-bone/80" data-id="element-388" data-mp="12mys0lak3qgx">
                        + Free U.S. shipping
                        {showCrystalBonus && bundle.bonus && ' + bonus crystal set'}
                      </p>}
                    <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-steel transition-colors duration-150 group-hover:text-bone" data-id="element-389" data-mp="2id802vamdvu6">
                      {formatPrice(bundle.price)} total · {formatPrice(bundle.price / bundle.count)}/mask
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" data-id="element-390" data-mp="24ei2zfnsvw5t" />
                    </p>
                  </button>
                </li>;
          })}
          </ol>
        </div>
      </div>
    </section>;
}